import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Facebook, Mail, Phone, MessageCircle } from "lucide-react";
import { Reveal } from "./Reveal";
import { NAV } from "@/data/site";
import { API, EMAIL, PHONE, PHONE_TEL, INSTAGRAM, FACEBOOK, waLink } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";

const Subscribe = () => {
  const { tr } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      toast.success(tr("You're subscribed. Thank you!", "¡Te has suscrito. Gracias!"));
      setEmail("");
    } catch {
      toast.error(tr("Please enter a valid email.", "Ingresa un correo válido."));
    }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="flex gap-2" data-testid="subscribe-form">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="flex-1 min-w-0 bg-[#131B21] border border-[#00C9B7]/20 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[#00C9B7]" data-testid="subscribe-email-input" />
      <button disabled={busy} className="rounded-full bg-[#00C9B7] text-[#031312] px-6 text-xs font-bold uppercase tracking-widest hover:bg-[#00E0CC] transition-colors disabled:opacity-60" data-testid="subscribe-submit-btn">{tr("Sign up", "Suscribirme")}</button>
    </form>
  );
};

const Social = ({ href, label, children }) => (
  <a href={href} target="_blank" rel="noreferrer" aria-label={label} className="w-11 h-11 rounded-full border border-[#00C9B7]/25 flex items-center justify-center text-slate-200 hover:bg-[#00C9B7] hover:text-[#031312] hover:-translate-y-1 transition-[transform,background-color,color] duration-300" data-testid={`footer-social-${label.toLowerCase()}`}>{children}</a>
);

export default function Footer() {
  const { tr, pick } = useLang();
  return (
    <footer className="relative border-t border-[#00C9B7]/10 bg-[#080B0D] overflow-hidden" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid gap-14 md:grid-cols-12">
        <Reveal className="md:col-span-5" from="left">
          <p className="font-display text-3xl text-[#1AA5A0] mb-4">Dr. Cora Baltodano</p>
          <p className="text-slate-400 italic mb-8">{tr("\"From Managua to San Juan Del Sur, caring is in my nature!\"", "\"¡De Managua a San Juan del Sur, cuidar está en mi naturaleza!\"")}</p>
          <p className="text-xs uppercase tracking-[0.3em] text-[#00C9B7] mb-3">{tr("Subscribe", "Suscríbete")}</p>
          <Subscribe />
        </Reveal>
        <Reveal className="md:col-span-3" delay={0.1}>
          <p className="text-xs uppercase tracking-[0.3em] text-[#00C9B7] mb-5">{tr("Explore", "Explorar")}</p>
          <ul className="space-y-3">
            {NAV.map((n) => <li key={n.to}><Link to={n.to} className="text-slate-300 hover:text-white transition-colors">{pick(n, "label")}</Link></li>)}
            <li><Link to="/dentist-san-juan-del-sur" className="text-slate-300 hover:text-white" data-testid="footer-sjds-link">{tr("Dentist in San Juan del Sur", "Dentista en San Juan del Sur")}</Link></li>
            <li><Link to="/dentist-managua" className="text-slate-300 hover:text-white" data-testid="footer-managua-link">{tr("Dentist in Managua", "Dentista en Managua")}</Link></li>
          </ul>
        </Reveal>
        <Reveal className="md:col-span-4" delay={0.2} from="right">
          <p className="text-xs uppercase tracking-[0.3em] text-[#00C9B7] mb-5">{tr("Connect With Us", "Conéctate con nosotros")}</p>
          <div className="space-y-3 mb-7 text-slate-300">
            <a href={`mailto:${EMAIL}`} className="flex items-center gap-3 hover:text-white" data-testid="footer-email-link"><Mail size={16} className="text-[#00C9B7]" /> {EMAIL}</a>
            <a href={PHONE_TEL} className="flex items-center gap-3 hover:text-white" data-testid="footer-phone-link"><Phone size={16} className="text-[#00C9B7]" /> {PHONE}</a>
            <a href={waLink()} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-white" data-testid="footer-whatsapp-link"><MessageCircle size={16} className="text-[#00C9B7]" /> {tr("Message us on WhatsApp!", "¡Escríbenos por WhatsApp!")}</a>
          </div>
          <div className="flex gap-3">
            <Social href={FACEBOOK} label="Facebook"><Facebook size={18} /></Social>
            <Social href={INSTAGRAM} label="Instagram"><Instagram size={18} /></Social>
            <Social href={waLink()} label="WhatsApp"><MessageCircle size={18} /></Social>
          </div>
        </Reveal>
      </div>
      <div className="border-t border-white/5 py-6 text-center text-xs text-slate-500">
        Copyright © {new Date().getFullYear()} Dr. Cora - {tr("All rights reserved.", "Todos los derechos reservados.")} · <Link to="/admin" className="hover:text-slate-300" data-testid="footer-admin-link">{tr("Owner login", "Acceso propietario")}</Link>
        <span className="block mt-2">{tr("Website Created by", "Sitio web creado por")} <a href="https://www.gregmiceli.com" target="_blank" rel="noopener" className="text-[#00C9B7] hover:underline" data-testid="footer-credit-link">Greg Miceli</a></span>
      </div>
    </footer>
  );
}
